/**
 * AddContractualChargeInline Component
 *
 * Inline form for adding contractual charges, similar to AddItem.
 * Shows when "Add Contractual Charge" button is clicked.
 */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CheckIcon, Icon } from 'utils/icons';
import FormInput from 'components/FormInput';
import './styles.scss';

// Helper function to format decimal values by removing trailing zeros
const formatDecimalValue = value => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  // Convert to number and back to string to remove trailing zeros
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return '';
  }
  // Convert to string, which will automatically remove trailing zeros
  return String(numValue);
};

const AddContractualChargeInline = ({
  onSave,
  onCancel,
  onDelete,
  budgetId,
  charge,
}) => {
  // The parent keys this form by budget/charge. Initialize once per edit session
  // so refreshed charge data cannot replace an unsaved draft or reset saving.
  const [name, setName] = useState(charge?.name || '');
  const [chargeType, setChargeType] = useState(
    charge?.charge_type || 'percentage'
  );
  const [percentage, setPercentage] = useState(
    formatDecimalValue(charge?.percentage)
  );
  const [flatAmount, setFlatAmount] = useState(
    formatDecimalValue(charge?.flat_amount)
  );
  const [isSaving, setIsSaving] = useState(false);
  const formRef = useRef(null);

  const handleSave = async () => {
    // Prevent double submission
    if (isSaving) {
      return;
    }

    // Validate form
    if (!name.trim()) {
      return;
    }

    if (
      chargeType === 'percentage' &&
      (!percentage || isNaN(Number(percentage)))
    ) {
      return;
    }

    if (
      chargeType === 'flat_fee' &&
      (!flatAmount || isNaN(Number(flatAmount)))
    ) {
      return;
    }

    if (!budgetId) {
      return;
    }

    const payload = {
      budget: Number(budgetId),
      name: name.trim(),
      charge_type: chargeType,
      percentage: chargeType === 'percentage' ? Number(percentage) : null,
      flat_amount: chargeType === 'flat_fee' ? Number(flatAmount) : null,
    };

    // Include id if editing
    if (charge?.id) {
      payload.id = charge.id;
    }

    setIsSaving(true);
    try {
      await onSave(payload);
      // Only reset and close if save was successful
      setName('');
      setChargeType('percentage');
      setPercentage('');
      setFlatAmount('');
      setIsSaving(false);
    } catch (error) {
      // Error is handled in the parent component, but keep form open on error
      setIsSaving(false);
      // Re-throw so parent can handle it
      throw error;
    }
  };

  const handleCancel = () => {
    // Don't allow canceling while saving
    if (isSaving) {
      return;
    }
    setName('');
    setChargeType('percentage');
    setPercentage('');
    setFlatAmount('');
    setIsSaving(false);
    onCancel();
  };

  const handleDelete = e => {
    e.preventDefault();
    e.stopPropagation();

    if (isSaving || !charge?.id || typeof onDelete !== 'function') {
      return;
    }

    onDelete(charge);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey && !isSaving) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape' && !isSaving) {
      e.preventDefault();
      e.stopPropagation();
      handleCancel();
    }
  };

  // Handle click outside to cancel
  useEffect(() => {
    const handleClickOutside = event => {
      // Don't cancel if saving
      if (isSaving) {
        return;
      }
      if (
        formRef.current &&
        !formRef.current.contains(event.target) &&
        !event.target.closest('.contractual-charge-add-inline')
      ) {
        handleCancel();
      }
    };

    // Use mousedown instead of click to catch the event before blur
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaving]); // Include isSaving to prevent canceling during save

  return (
    <div className="swimlane-item swimlane-item--add-input contractual-charge-add-inline">
      <div
        className={`contractual-charge-add-inline__form ${
          isSaving ? 'contractual-charge-add-inline__form--saving' : ''
        }`}
        ref={formRef}
      >
        <div className="contractual-charge-add-inline__row">
          <FormInput
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Title"
            autoFocus={!isSaving}
            style={{ width: '230px' }}
            addSpacing={false}
            disabled={isSaving}
          />
        </div>

        <div className="contractual-charge-add-inline__row">
          <div className="type-selector">
            <button
              type="button"
              className={`type-button ${
                chargeType === 'percentage' ? 'active' : ''
              }`}
              onClick={() => {
                if (isSaving) return;
                // When switching to percentage, preserve flatAmount value if percentage is empty
                if (!percentage && flatAmount) {
                  setPercentage(flatAmount);
                }
                setChargeType('percentage');
              }}
              disabled={isSaving}
            >
              Percentage
            </button>
            <button
              type="button"
              className={`type-button ${
                chargeType === 'flat_fee' ? 'active' : ''
              }`}
              onClick={() => {
                if (isSaving) return;
                // When switching to flat_fee, preserve percentage value if flatAmount is empty
                if (!flatAmount && percentage) {
                  setFlatAmount(percentage);
                }
                setChargeType('flat_fee');
              }}
              disabled={isSaving}
            >
              Flat Fee
            </button>
          </div>
        </div>

        <div className="contractual-charge-add-inline__row">
          <div>
            {chargeType === 'percentage' ? (
              <FormInput
                type="number"
                value={percentage}
                onChange={e => setPercentage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Percentage"
                min="0"
                max="100"
                addSpacing={false}
                disabled={isSaving}
              />
            ) : (
              <FormInput
                type="number"
                value={flatAmount}
                onChange={e => setFlatAmount(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Amount"
                min="0"
                addSpacing={false}
                disabled={isSaving}
              />
            )}
          </div>
          <div className="contractual-charge-add-inline__actions">
            {charge?.id && typeof onDelete === 'function' && (
              <button
                type="button"
                className="contractual-charge-add-inline__action contractual-charge-add-inline__action--delete"
                aria-label={`Delete ${charge.name}`}
                title="Delete contractual charge"
                onClick={handleDelete}
                disabled={isSaving}
              >
                <Icon name="TrashCanIcon" />
              </button>
            )}
            <div
              className={`add-item-input__container__add-action ${
                isSaving ? 'add-item-input__container__add-action--saving' : ''
              }`}
              onClick={handleSave}
              onMouseDown={e => {
                if (!isSaving) {
                  e.preventDefault();
                }
              }}
            >
              {isSaving ? (
                <div className="contractual-charge-add-inline__spinner" />
              ) : (
                <CheckIcon />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AddContractualChargeInline.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  charge: PropTypes.object,
};

AddContractualChargeInline.defaultProps = {
  onDelete: null,
  charge: null,
};

export default AddContractualChargeInline;
